import {
  countries,
  deleteAllWithSameProperties,
  hasAtLeastOneWithSameProperties,
  isValidEmail,
  languages,
  useTranslation,
} from '@gimlite/common';
import {
  AutoComplete,
  AutoCompleteType,
  Checkbox,
  Col,
  DatePicker,
  DatePickerType,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  TextArea,
  useStoreUIV1,
  Widget,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo } from 'react';
import {
  AntiPassBackMode,
  ArticleCode,
  ArticleCodeStatus,
} from '../../client/graphql';
import { DefaultContractDetailsWizard } from '../../common/defaultValue/wizard';
import { APBModeGlobal } from '../../common/mapper/abp.mapper';
import {
  CredentialType,
  downloadQrCode,
  globalSortCredentialsFn,
} from '../../common/mapper/credential.mapper';
import {
  CredentialCard,
  CredentialCardGroupType,
} from '../../components/credential/credential.component';
import {
  PLATECredentialCard,
  PLATECredentialStatic,
} from '../../composition/credential/plate.credential';
import { QRCODECredentialCard } from '../../composition/credential/qrcode.credential';
import {
  RFIDCredentialCard,
  RFIDCredentialStatic,
} from '../../composition/credential/rfid.credential';
import { TCSCredentialCard } from '../../composition/credential/tcs.credential';
import { config } from '../../config';

export declare namespace ContractBaseFormType {
  type Props = {
    data: {
      contract: ContractBaseFormType.Data.ContractBase;
    };
    config: {
      parentContract?: ContractBaseFormType.Data.ContractBase | null;
      disabled?: {
        endDate?: boolean;
      };
      articleCodes: Array<ArticleCode>;
      withCapacity?: boolean;
    };
  };

  namespace Data {
    type ContractBase = {
      startDate: string | null;
      endDate: string | null;
      APBMode: AntiPassBackMode | null;
      APBNext: boolean | null;
      reference: string | null;
      description: string | null;
      state: boolean;
      capacity?: number | null;
      articleCode: string | null;
    };
  }
}

export const ContractBaseForm = ({
  data: { contract },
  config: { disabled, parentContract, articleCodes, withCapacity = false },
}: ContractBaseFormType.Props) => {
  const { t } = useTranslation();

  console.log({
    contract,
    disabled,
    parentContract,
    articleCodes,
    withCapacity,
  });

  const startDateLimit = useMemo((): {
    min: DatePickerType.Config.Min | null;
    max: DatePickerType.Config.Max | null;
  } => {
    return {
      min: parentContract?.startDate || new Date().toISOString(),
      max: disabled?.endDate ? null : contract.endDate,
    };
  }, [parentContract?.startDate, contract.endDate, disabled?.endDate]);

  const endDateLimit = useMemo((): {
    min: DatePickerType.Config.Min | null;
    max: DatePickerType.Config.Max | null;
  } => {
    return {
      min: contract.startDate || new Date().toISOString(),
      max: parentContract?.endDate || null,
    };
  }, [contract.startDate, parentContract?.endDate]);

  return (
    <Zone
      config={{
        zones: [['validity', 'other']],
        rows: ['1fr'],
        columns: ['1fr', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'validity' }}>
        <Widget config={{ title: t('validity'), shadow: false }}>
          <Col>
            <Form.Item
              data={{
                defaultValue: contract.startDate,
              }}
              config={{
                name: 'startDate',
                label: t('startDate'),
                rules: [{ required: true }],
                way: 'vertical',
              }}
            >
              <DatePicker
                config={{
                  defaultTime: 'startDay',
                  width: 'full',
                  format: 'datetime',
                  min: startDateLimit.min,
                  max: startDateLimit.max,
                }}
              />
            </Form.Item>
            <Space config={{ count: 2 }} />
            <Form.Item
              data={{ defaultValue: contract.endDate }}
              config={{
                name: 'endDate',
                label: t('endDate'),
                rules: [{ required: true }],
                way: 'vertical',
              }}
            >
              <DatePicker
                config={{
                  defaultTime: 'endDay',
                  disabled: !!disabled?.endDate,
                  width: 'full',
                  format: 'datetime',
                  min: endDateLimit.min,
                  max: endDateLimit.max,
                }}
              />
            </Form.Item>
            <Space config={{ count: 2 }} />
            <Form.Item
              data={{
                defaultValue: contract.articleCode,
              }}
              config={{
                name: 'articleCode',
                label: t('codePMS'),
                way: 'vertical',
                rules: [{ required: true }],
              }}
            >
              <Select
                data={{
                  items: articleCodes
                    .filter(({ status }) => status === ArticleCodeStatus.Active)
                    .map(({ code }) => ({
                      label: code,
                      value: code,
                    })),
                }}
                config={{ width: 'full' }}
              />
            </Form.Item>
            <Space config={{ count: 2 }} />
            <Form.Item
              data={{ defaultValue: contract.state }}
              config={{
                name: 'state',
                label: t('stateOfContract'),
                rules: [{ required: true }],
                way: 'horizontal',
              }}
            >
              <Switch
                config={{
                  width: 'full',
                  color: { active: 'success', inactive: 'warn' },
                  text: { active: t('active'), inactive: t('suspend') },
                }}
              />
            </Form.Item>
          </Col>
        </Widget>
      </Zone.Area>

      <Zone.Area config={{ area: 'other' }}>
        <Widget config={{ title: t('otherParams'), shadow: false }}>
          <Col>
            {withCapacity && (
              <>
                <Form.Item
                  data={{
                    defaultValue: contract.capacity,
                  }}
                  config={{
                    name: 'capacity',
                    label: t('capacity'),
                    rules: [{ required: true }],
                    way: 'vertical',
                  }}
                >
                  <Input config={{ width: 'full', type: { name: 'number' } }} />
                </Form.Item>
                <Space config={{ count: 2 }} />
              </>
            )}

            <Form.Item
              data={{ defaultValue: contract.reference }}
              config={{
                name: 'reference',
                label: t('reference'),
                way: 'vertical',
              }}
            >
              <Input config={{ width: 'full' }} />
            </Form.Item>
            <Space config={{ count: 2 }} />
            <Form.Item
              data={{ defaultValue: contract.description }}
              config={{
                name: 'description',
                label: t('description'),
                way: 'vertical',
              }}
            >
              <TextArea config={{ width: 'full' }} />
            </Form.Item>
            <Space config={{ count: 2 }} />
            <Form.Item
              data={{
                defaultValue: contract.APBMode,
              }}
              config={{
                name: 'APBMode',
                label: t('APBMode'),
                tooltip: (
                  <Write
                    data={{
                      item: t('TOOLPIP_PRESENT_ANTIPASSBACK_MODE'),
                    }}
                    config={{ mode: 'key-small-bold' }}
                  />
                ),
                way: 'horizontal',
              }}
            >
              <Select
                data={{
                  items: APBModeGlobal.map((value) => ({
                    label: t(value.toLowerCase()),
                    value,
                  })),
                }}
                config={{ width: 'full' }}
              />
            </Form.Item>
            <Space config={{ count: 2 }} />
            {contract.APBMode !== 'NONE' && !!contract.state && (
              <Form.Item
                data={{ defaultValue: contract.APBNext }}
                config={{
                  name: 'APBNext',
                  label: t('APBNext'),
                  way: 'horizontal',
                  tooltip: (
                    <Write
                      data={{
                        item: t('TOOLPIP_PRESENT_ANTIPASSBACK_NEXT'),
                      }}
                      config={{ mode: 'key-small-bold' }}
                    />
                  ),
                }}
              >
                <Switch
                  config={{
                    width: 'full',
                    color: { active: 'success', inactive: 'warn' },
                    text: { active: t('active'), inactive: t('suspend') },
                  }}
                />
              </Form.Item>
            )}
          </Col>
        </Widget>
      </Zone.Area>
    </Zone>
  );
};

export declare namespace ContractDetailsFormType {
  type Props = {
    handleEvent: {
      submitCredentials: (
        data: ContractDetailsFormType.Data.Credentials,
      ) => void;
      submitContract: (
        data: ContractDetailsFormType.Data.ContractDetails,
      ) => void;
      validate: (isValid: boolean) => void;
    };
    data: {
      credentials: ContractDetailsFormType.Data.Credentials;
      contract: ContractDetailsFormType.Data.ContractDetails;
    };
    config?: {
      credentialsEdit?: CredentialCardGroupType.Config.Credentials;
      search?: {
        selectContract: (value: AutoCompleteType.Data.Item | null) => void;
        clearSearchContract: () => void;
        searchContract: (value: AutoCompleteType.Data.Item['value']) => void;
        submitSearchContractValue: (
          data: AutoCompleteType.Data.Item | null,
        ) => void;
        currentSearchContractItems: AutoCompleteType.Data.Item[];
        currentSearchContractValue: ContractDetailsFormType.Data.CurrentSearchContractValue;
      };
    };
  };

  namespace Data {
    type Credentials = {
      actived: CredentialType.CredentialFromFront[];
      desactived: CredentialType.CredentialFromFront[];
      context: CredentialCardGroupType.Config.Place;
    };

    type ContractDetails = {
      _id: string;
      name1: string | null;
      name2: string | null;
      address1: string | null;
      address2: string | null;
      city: string | null;
      zipcode: string | null;
      country: string | null;
      username: string | null;
      lang: string | null;
      contractId: string | null;
      motoristId: string | null;
      gender: string | null;
      shortUid: string | null;
      inviteMotorist: boolean;
    };

    type CurrentSearchContractValue = AutoCompleteType.Data.Item | null;
  }
}

export const ContractDetailsForm = observer(
  ({
    data: { credentials, contract },
    handleEvent: { submitCredentials, submitContract, validate },
    config: { search, credentialsEdit } = {},
  }: ContractDetailsFormType.Props) => {
    const { t, lang } = useTranslation();
    const { AppStore } = useStoreUIV1();

    const cardIsActived = useCallback(
      (value: CredentialType.CredentialFromFront): boolean => {
        return !hasAtLeastOneWithSameProperties(credentials.desactived, value);
      },
      [credentials],
    );

    const cardCreate = useCallback(
      (value: CredentialType.CredentialFromFront) => {
        submitCredentials({
          desactived: credentials.desactived,
          context: credentials.context,
          actived: [
            ...credentials.actived,
            {
              ...value,
            },
          ],
        });
      },
      [credentials],
    );

    const cardDelete = useCallback(
      (value: CredentialType.CredentialFromFront) => {
        submitCredentials({
          desactived: credentials.desactived,
          context: credentials.context,
          actived:
            deleteAllWithSameProperties<CredentialType.CredentialFromFront>(
              credentials.actived,
              value,
            ),
        });
      },
      [credentials],
    );

    const cardImport = useCallback(
      (value: CredentialType.CredentialFromFront) => {
        submitCredentials({
          actived: credentials.actived,
          context: credentials.context,
          desactived:
            deleteAllWithSameProperties<CredentialType.CredentialFromFront>(
              credentials.desactived,
              value,
            ),
        });
      },
      [credentials],
    );

    const cardUnload = useCallback(
      (value: CredentialType.CredentialFromFront) => {
        submitCredentials({
          actived: credentials.actived,
          context: credentials.context,
          desactived: [...credentials.desactived, value],
        });
      },
      [credentials],
    );

    useEffect(() => {
      if (lang && !contract.lang) {
        submitContract({ ...contract, lang });
      }
    }, [contract, lang]);

    useEffect(() => {
      validate(true);
    }, [contract, credentials]);

    return (
      <Col config={{ height: 'full', scrollY: true }}>
        <Row>
          <Col>
            {search ? (
              <Widget config={{ shadow: false }}>
                <AutoComplete
                  handleEvent={{
                    clear: () => {
                      submitCredentials({
                        actived: [],
                        desactived: [],
                        context: 'wizardNewContract',
                      });
                      submitContract(DefaultContractDetailsWizard({ t, lang }));
                    },
                    select: (value) => {
                      search.selectContract(value);
                    },
                    search: (value) => {
                      search.submitSearchContractValue(value);
                    },
                    input: (value) => {
                      value &&
                        (value.length >= 3
                          ? search.searchContract?.(value)
                          : search.clearSearchContract?.());

                      if (credentials.context === 'wizardContractExist') {
                        submitCredentials({
                          actived: [],
                          desactived: [],
                          context: 'wizardNewContract',
                        });

                        submitContract(
                          DefaultContractDetailsWizard({ t, lang }),
                        );
                      }
                    },
                  }}
                  data={{
                    items: search.currentSearchContractItems,
                    value: search.currentSearchContractValue,
                  }}
                  config={{
                    search: {
                      accentSensitive: false,
                      caseSensitive: false,
                      pattern: 'everywhere',
                    },
                    height: 'medium',
                    width: 'full',
                    placeholder: t('searchAExistingContract'),
                    design: 'search',
                  }}
                />
              </Widget>
            ) : (
              <></>
            )}
          </Col>
          <Col>
            <></>
          </Col>
        </Row>
        <Row>
          <Col config={{ width: 'full' }}>
            <Col config={{ width: 'full' }}>
              <Widget config={{ title: t('identity'), shadow: false }}>
                <Form.Item
                  data={{ defaultValue: contract.username }}
                  config={{
                    name: 'username',
                    label: t('email'),
                    rules: [{ required: true }, { validator: isValidEmail }],
                    way: 'vertical',
                  }}
                >
                  <Input
                    config={{
                      width: 'full',
                      type: { name: 'email' },
                      disabled:
                        credentials.context === 'wizardContractExist' &&
                        !!contract.motoristId,
                    }}
                  />
                </Form.Item>
                <Space config={{ count: 1.5 }} />
                <Row>
                  <Form.Item
                    data={{
                      defaultValue: contract.name1,
                    }}
                    config={{
                      name: 'name1',
                      label: t('firstName'),
                      rules: [{ required: true }],
                      way: 'vertical',
                    }}
                  >
                    <Input
                      config={{
                        width: 'full',
                        disabled:
                          credentials.context === 'wizardContractExist' &&
                          !!contract.motoristId,
                      }}
                    />
                  </Form.Item>
                  <Space config={{ way: 'horizontal', count: 1.5 }} />
                  <Form.Item
                    data={{
                      defaultValue: contract.name2,
                    }}
                    config={{
                      name: 'name2',
                      label: t('lastName'),
                      rules: [{ required: true }],
                      way: 'vertical',
                    }}
                  >
                    <Input
                      config={{
                        width: 'full',
                        disabled:
                          credentials.context === 'wizardContractExist' &&
                          !!contract.motoristId,
                      }}
                    />
                  </Form.Item>
                </Row>
                <Space config={{ count: 1.5 }} />
                <Form.Item
                  data={{ defaultValue: contract.lang }}
                  config={{
                    name: 'lang',
                    label: t('language'),
                    way: 'vertical',
                  }}
                >
                  <Select
                    data={{ items: lang ? languages[lang] : [] }}
                    config={{
                      width: 'full',
                      disabled:
                        credentials.context === 'wizardContractExist' &&
                        !!contract.motoristId,
                    }}
                  />
                </Form.Item>

                <Space config={{ count: 1.5 }} />
              </Widget>
            </Col>

            <Col config={{ width: 'full' }}>
              <Widget config={{ title: t('address'), shadow: false }}>
                <Form.Item
                  data={{ defaultValue: contract.address1 }}
                  config={{
                    name: 'address1',
                    label: t('address1'),
                    way: 'vertical',
                  }}
                >
                  <Input
                    config={{
                      width: 'full',
                      disabled:
                        credentials.context === 'wizardContractExist' &&
                        !!contract.motoristId,
                    }}
                  />
                </Form.Item>

                <Space config={{ count: 1.5 }} />

                <Form.Item
                  data={{ defaultValue: contract.address2 }}
                  config={{
                    name: 'address2',
                    label: t('address2'),
                    way: 'vertical',
                  }}
                >
                  <Input
                    config={{
                      width: 'full',
                      disabled:
                        credentials.context === 'wizardContractExist' &&
                        !!contract.motoristId,
                    }}
                  />
                </Form.Item>

                <Space config={{ count: 1.5 }} />
                <Row>
                  <Form.Item
                    data={{ defaultValue: contract.zipcode }}
                    config={{
                      name: 'zipcode',
                      label: t('zipCode'),
                      way: 'vertical',
                    }}
                  >
                    <Input
                      config={{
                        width: 'small',
                        disabled:
                          credentials.context === 'wizardContractExist' &&
                          !!contract.motoristId,
                      }}
                    />
                  </Form.Item>
                  <Space config={{ way: 'horizontal', count: 1.5 }} />
                  <Form.Item
                    data={{ defaultValue: contract.city }}
                    config={{
                      name: 'city',
                      label: t('city'),
                      way: 'vertical',
                    }}
                  >
                    <Input
                      config={{
                        width: 'full',
                        disabled:
                          credentials.context === 'wizardContractExist' &&
                          !!contract.motoristId,
                      }}
                    />
                  </Form.Item>
                </Row>
                <Space config={{ count: 1.5 }} />

                <Form.Item
                  data={{
                    defaultValue: contract.country,
                  }}
                  config={{
                    name: 'country',
                    label: t('country'),
                    way: 'vertical',
                  }}
                >
                  <Select
                    data={{
                      items: lang
                        ? countries[lang].map(({ label, value }) => ({
                            label,
                            value: value.ISO3,
                          }))
                        : [],
                    }}
                    config={{
                      width: 'full',
                      disabled:
                        credentials.context === 'wizardContractExist' &&
                        !!contract.motoristId,
                    }}
                  />
                </Form.Item>
              </Widget>
            </Col>
          </Col>
          <Col>
            <Zone
              config={{
                zones: [['configuration'], ['credentials']],
                rows: ['min-content', '1fr'],
                columns: ['1fr'],
              }}
            >
              <Zone.Area config={{ area: 'configuration' }}>
                <Widget config={{ title: t('configuration'), shadow: false }}>
                  <Form.Item
                    data={{ defaultValue: contract.inviteMotorist }}
                    config={{
                      name: 'inviteMotorist',
                      label: t('sendInvitationEmailToMotorist'),
                      way: 'horizontal',
                    }}
                  >
                    <Checkbox
                      config={{
                        size: 'xlarge',
                        disabled: !!contract.motoristId,
                      }}
                    />
                  </Form.Item>
                </Widget>
              </Zone.Area>
              <Zone.Area config={{ area: 'credentials' }}>
                <Widget config={{ title: t('credentials'), shadow: false }}>
                  <Col
                    config={{
                      horizontal: 'start',
                      vertical: 'start',
                      scrollY: true,
                      height: 'full',
                    }}
                  >
                    <CredentialCard.Group
                      handleEvent={{
                        event: ({ event, newValue }) => {
                          return new Promise((resolve, reject) => {
                            if (event === 'create') {
                              cardCreate(
                                newValue as CredentialType.CredentialFromFront,
                              );

                              resolve('');
                            }

                            reject('UNKONW_ERROR');
                          });
                        },
                      }}
                      config={{
                        place: credentials.context,
                        vertical: 'start',
                        credentials: credentialsEdit,
                      }}
                    >
                      {credentials.actived
                        .sort(globalSortCredentialsFn)
                        .map(
                          (
                            credential: CredentialType.CredentialFromFront,
                            index: number,
                          ) => (
                            <div key={index}>
                              {credential.name === 'QRCODE' ? (
                                <QRCODECredentialCard
                                  handleEvent={{
                                    event: async (value) => {
                                      await downloadQrCode({
                                        contractId: contract._id,
                                        host: config.BFF_OSP_MOTORISTS_HOST!,
                                        token: config.BFF_OSP_MOTORIST_TOKEN!,
                                      });
                                    },
                                  }}
                                  data={{
                                    _id: crypto.randomUUID(),
                                    generatedAt: credential.generatedAt,
                                    value: credential.value,
                                  }}
                                />
                              ) : credential.name === 'TCS' ? (
                                <TCSCredentialCard
                                  handleEvent={{
                                    event: ({ event, name, primaryValue }) => {
                                      switch (event) {
                                        case 'delete':
                                          cardDelete({
                                            name,
                                            externalId:
                                              credential.externalId ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;

                                        case 'import':
                                          cardImport({
                                            _id: credential._id,
                                            name,
                                            externalId:
                                              credential.externalId ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;

                                        case 'unload':
                                          cardUnload({
                                            _id: credential._id,
                                            name,
                                            externalId:
                                              credential.externalId ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;
                                      }
                                    },
                                  }}
                                  config={{
                                    isActive: cardIsActived({
                                      _id: credential._id,
                                      name: credential.name,
                                      externalId: credential.externalId ?? null,
                                    }),
                                  }}
                                  data={{
                                    _id: credential._id,
                                    externalId: credential.externalId,
                                  }}
                                />
                              ) : credential.name === 'RFID' ? (
                                <RFIDCredentialCard
                                  config={{
                                    isActive: cardIsActived({
                                      _id: credential._id,
                                      name: credential.name,
                                      uid: credential.uid ?? null,
                                      visibleId: credential.visibleId ?? null,
                                    }),
                                  }}
                                  handleEvent={{
                                    event: ({ event, name, primaryValue }) => {
                                      switch (event) {
                                        case 'delete':
                                          cardDelete({
                                            name,
                                            [RFIDCredentialStatic.info
                                              .primaryValue]:
                                              primaryValue ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;

                                        case 'import':
                                          cardImport({
                                            _id: credential._id,
                                            name,
                                            uid: credential.uid ?? null,
                                            visibleId:
                                              credential.visibleId ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;

                                        case 'unload':
                                          cardUnload({
                                            _id: credential._id,
                                            name,
                                            uid: credential.uid ?? null,
                                            visibleId:
                                              credential.visibleId ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;
                                      }
                                    },
                                  }}
                                  data={{
                                    _id: credential._id,
                                    uid: credential.uid,
                                    visibleId: credential.visibleId,
                                  }}
                                />
                              ) : (
                                <PLATECredentialCard
                                  config={{
                                    isActive: cardIsActived({
                                      _id: credential._id,
                                      name: credential.name,
                                      description:
                                        credential.description ?? null,
                                      plate: credential.plate ?? null,
                                    }),
                                  }}
                                  handleEvent={{
                                    event: ({ event, name, primaryValue }) => {
                                      switch (event) {
                                        case 'delete':
                                          cardDelete({
                                            name: credential.name,
                                            [PLATECredentialStatic.info
                                              .primaryValue]:
                                              primaryValue ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;

                                        case 'import':
                                          cardImport({
                                            _id: credential._id,
                                            name: credential.name,
                                            description:
                                              credential.description ?? null,
                                            plate: credential.plate ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;

                                        case 'unload':
                                          cardUnload({
                                            _id: credential._id,
                                            name: credential.name,
                                            description:
                                              credential.description ?? null,
                                            plate: credential.plate ?? null,
                                          } as CredentialType.CredentialFromFront);

                                          break;
                                      }
                                    },
                                  }}
                                  data={{
                                    _id: credential._id,
                                    description: credential.description,
                                    plate: credential.plate,
                                  }}
                                />
                              )}
                            </div>
                          ),
                        )}
                    </CredentialCard.Group>
                  </Col>
                </Widget>
              </Zone.Area>
            </Zone>
          </Col>
        </Row>
      </Col>
    );
  },
);
